import { theme } from 'antd';
import React, { createContext, useContext, useEffect, useState } from 'react';
import { updateUserProfile } from '../services/userApi';
import { useAuth } from './AuthContext';
import { isAdaptiveAuthPath } from '../components/Marketing/adaptivePaths';

// LocalStorage key for theme preference
const THEME_PREFERENCE_KEY = 'cellcog_theme_preference';

// react-snap prerender guard: snapshots must stay the dark fail-safe face —
// the snapshot machine's own OS scheme must never be baked into static HTML
// (same rule as the data-mkt-theme boot script in public/index.html).
const IS_PRERENDER =
  typeof navigator !== 'undefined' && /ReactSnap/i.test(navigator.userAgent);

const ThemeContext = createContext();

export const ThemeProvider = ({ children }) => {
  // Initialize theme from localStorage or default to SYSTEM
  const getInitialTheme = () => {
    const savedTheme = localStorage.getItem(THEME_PREFERENCE_KEY);
    return savedTheme || 'SYSTEM';
  };

  const [themePreference, setThemePreference] = useState(getInitialTheme);
  // Resolved synchronously so the very first render already knows the OS
  // scheme — with the adaptive auth pages (CEL-1627) an async 'SYSTEM'
  // placeholder would paint one light frame for dark-OS visitors before the
  // matchMedia effect below caught up. The effect still owns live changes.
  const [systemTheme, setSystemTheme] = useState(() =>
    typeof window !== 'undefined' &&
    window.matchMedia &&
    window.matchMedia('(prefers-color-scheme: dark)').matches
      ? 'DARK'
      : 'LIGHT'
  );
  // Adaptive AUTH pages (/login, /signup — adaptivePaths.js): the one
  // logged-out surface where the app theme follows the visitor instead of
  // the dark pin (CEL-1627). Fed by AdaptiveAuthThemeBridge in App.js on
  // every navigation (ThemeProvider sits above Router, so it cannot
  // useLocation itself); initialized from the URL so a direct load resolves
  // before the first render.
  const [adaptiveAuthPath, setAdaptiveAuthPath] = useState(() =>
    typeof window !== 'undefined' && isAdaptiveAuthPath(window.location.pathname)
  );
  const { currentUser, userProfile } = useAuth();

  // Use the theme preference from the user profile when it loads
  useEffect(() => {
    if (userProfile && userProfile.theme_preference) {
      setThemePreference(userProfile.theme_preference);
      // Update localStorage with user profile preference
      localStorage.setItem(THEME_PREFERENCE_KEY, userProfile.theme_preference);
    }
  }, [userProfile]);

  // Detect system theme preference
  useEffect(() => {
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    setSystemTheme(mediaQuery.matches ? 'DARK' : 'LIGHT');

    const handleChange = (e) => {
      setSystemTheme(e.matches ? 'DARK' : 'LIGHT');
    };

    // Use the correct event listener method based on browser support
    if (mediaQuery.addEventListener) {
      mediaQuery.addEventListener('change', handleChange);
      return () => mediaQuery.removeEventListener('change', handleChange);
    } else {
      // Fallback for older browsers
      mediaQuery.addListener(handleChange);
      return () => mediaQuery.removeListener(handleChange);
    }
  }, []);

  // Update theme preference and persist it
  const changeTheme = async (newTheme) => {
    setThemePreference(newTheme);
    // Update localStorage immediately
    localStorage.setItem(THEME_PREFERENCE_KEY, newTheme);

    // Also update on the server if user is logged in
    if (currentUser) {
      try {
        await updateUserProfile({ theme_preference: newTheme });
      } catch (error) {
        console.error('Failed to update user theme preference:', error);
      }
    }
  };

  // Logged-out visitors get the DARK-LOCKED marketing surface: force dark
  // regardless of system/OS preference, so the logo variant, primary color,
  // and every isDarkMode-driven element are deterministically dark. Logged-in
  // users keep full DARK/LIGHT/SYSTEM control.
  // ONE exception (CEL-1627): the system-adaptive AUTH pages (/login,
  // /signup) resolve the real preference/OS theme for logged-out visitors
  // too, so antd's algorithm + the [data-theme] auth CSS render the same
  // face as html[data-mkt-theme] (both derive from identical inputs:
  // explicit preference wins, else the OS). Never during react-snap
  // prerender — the baked snapshot stays the dark fail-safe face.
  const resolvedDarkMode = themePreference === 'DARK' || (themePreference === 'SYSTEM' && systemTheme === 'DARK');
  const isDarkMode = currentUser || (adaptiveAuthPath && !IS_PRERENDER) ? resolvedDarkMode : true;

  // SINGLE WRITER of document-level theme side effects (CEL-1359). data-theme
  // once had TWO writers (here + MarketingLayout's mount-pin/unmount-restore);
  // a stale write while isDarkMode was stable left a PERMANENT split — CSS
  // keyed on [data-theme] rendered dark while antd/isDarkMode rendered light
  // (the mixed /welcome + dark-sidebar/light-panel screens, light-OS new
  // signups). With one writer keyed on isDarkMode, the attribute converges on
  // every mount and every change, and no other code path can desync it.
  // The body background baseline lives here too: the prerendered HTML bakes
  // #0a0b0d inline on <body>, which a light-resolved account must have
  // cleared by the same authority (dark keeps the near-black baseline that
  // both the marketing shell and the dark app sit on).
  useEffect(() => {
    document.documentElement.setAttribute('data-theme', isDarkMode ? 'dark' : 'light');
    document.body.style.backgroundColor = isDarkMode ? '#0a0b0d' : '';
  }, [isDarkMode]);

  // Custom dark algorithm that preserves exact colorPrimary
  const customDarkAlgorithm = (seedToken) => {
    // First apply Ant Design's dark algorithm for all other tokens
    const derivedToken = theme.darkAlgorithm(seedToken);
    
    // Override to preserve our exact primary color (instead of darkened version)
    derivedToken.colorPrimary = seedToken.colorPrimary;
    
    // Also preserve in derived color palettes if needed
    // The algorithm generates color-1 through color-10, we want color-6 (primary) to be exact
    if (derivedToken.colorPrimaryBg) {
      // You may need to adjust other derived colors if they look off
      // For now, just preserve the main colorPrimary
    }
    
    return derivedToken;
  };

  const themeConfig = {
    // Use custom algorithm in dark mode to preserve exact colorPrimary
    algorithm: isDarkMode ? customDarkAlgorithm : theme.defaultAlgorithm,
    token: {
      // Theme-aware primary color - will be preserved exactly in dark mode
      colorPrimary: isDarkMode ? '#7dc3c3' : '#2e7e7e',
      
      // Theme-aware primary button text color
      colorTextLightSolid: isDarkMode ? '#0a1a1a' : '#ffffff',
      
      colorBgContainer: isDarkMode ? '#141414' : '#ffffff',
      colorBgElevated: isDarkMode ? '#1f1f1f' : '#ffffff',
      colorText: isDarkMode ? '#ffffff' : '#000000',
      
      // Update link colors to match new primary
      colorLink: isDarkMode ? '#7dc3c3' : '#2e7e7e',
      colorLinkHover: isDarkMode ? '#8dcfcf' : '#3e8e8e',
      colorLinkActive: isDarkMode ? '#6db3b3' : '#257373',
    },
    components: {
      Tooltip: {
        // Slack-style INVERTED contrast (Nitish 2026-08-11): tooltips must pop against their
        // theme — dark pill on the light theme, light pill with dark text on the dark theme.
        // A dark-on-dark tooltip disappears into the page. Neutrals match the channel CSS
        // text palette (#f2f2f7 / #1d1d1f) so the pill reads native to the app.
        colorBgSpotlight: isDarkMode ? '#f2f2f7' : 'rgba(0, 0, 0, 0.85)',
        colorTextLightSolid: isDarkMode ? '#1d1d1f' : '#ffffff',
      }
    }
  };

  return (
    <ThemeContext.Provider value={{ themePreference, changeTheme, theme: themeConfig, isDarkMode, setAdaptiveAuthPath }}>
      {children}
    </ThemeContext.Provider>
  );
};

export const useTheme = () => useContext(ThemeContext);
